import { useCallback, useMemo } from 'react';

import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';

import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/utils';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { useCreateRovoExperienceSidebarController } from '@confluence/ai-rovo-button/entry-points/useCreateRovoExperienceSidebarController.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

type UseToggleConversationAssistantReturnType = {
	mode: ConversationAssistantUIModes | undefined;
	toggleConversationAssistant: (
		openChatMode?: RovoChatOpenMode,
		autoFocusInput?: boolean,
		openAtMaxSize?: boolean,
	) => void;
	/**
	 * Opens the assistant in the given mode. Mode is resolved exactly as it is for any other open.
	 */
	openConversationAssistant: (
		openChatMode?: RovoChatOpenMode,
		autoFocusInput?: boolean,
		openAtMaxSize?: boolean,
	) => void;
	isConversationAssistantSidebarActive: boolean;
	/** Grows an already-open mini-modal to full size. */
	forceMiniModalFullSize: () => void;
};

/**
 * Note: this only works if Rovo is enabled, look up RovoButton as an example
 * @returns toggleConversationAssistant which will open the given mode of the Conversation Assistant
 */
export const useToggleConversationAssistant = (): UseToggleConversationAssistantReturnType => {
	const rightSidebarContext = useRightSidebarContext();
	const { panelId } = rightSidebarContext.getCurrent() || {};
	const {
		mode,
		isMiniModalAvailable,
		toggleConversationAssistantUIMode,
		openConversationAssistantUIMode,
		forceMiniModalFullSize: forceMiniModalFullSizeInStore,
	} = useRovoExperienceAPIStore();
	const isConversationAssistantSidebarActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID;
	const sidebarApi = useCreateRovoExperienceSidebarController();

	const toggleConversationAssistant = useCallback(
		(
			openChatMode: RovoChatOpenMode = 'sidebar',
			autoFocusInput = true,
			openAtMaxSize?: boolean,
		) => {
			toggleConversationAssistantUIMode({
				targetMode:
					openChatMode === 'mini-modal' && isMiniModalAvailable
						? ConversationAssistantUIModes.MINI_MODAL
						: ConversationAssistantUIModes.SIDEBAR,
				sidebarApi,
				autoFocusInput,
				...(openChatMode === 'mini-modal' && isMiniModalAvailable && openAtMaxSize
					? { openAtMaxSize }
					: {}),
			});
		},
		[toggleConversationAssistantUIMode, sidebarApi, isMiniModalAvailable],
	);

	const openConversationAssistant = useCallback(
		(
			openChatMode: RovoChatOpenMode = 'sidebar',
			autoFocusInput = true,
			openAtMaxSize?: boolean,
		) => {
			openConversationAssistantUIMode({
				targetMode:
					openChatMode === 'mini-modal' && isMiniModalAvailable
						? ConversationAssistantUIModes.MINI_MODAL
						: ConversationAssistantUIModes.SIDEBAR,
				sidebarApi,
				autoFocusInput,
				...(openChatMode === 'mini-modal' && isMiniModalAvailable && openAtMaxSize
					? { openAtMaxSize }
					: {}),
			});
		},
		[openConversationAssistantUIMode, sidebarApi, isMiniModalAvailable],
	);

	const forceMiniModalFullSize = useCallback(() => {
		forceMiniModalFullSizeInStore?.();
	}, [forceMiniModalFullSizeInStore]);

	return useMemo(
		() => ({
			mode,
			toggleConversationAssistant,
			openConversationAssistant,
			isConversationAssistantSidebarActive,
			forceMiniModalFullSize,
		}),
		[
			mode,
			toggleConversationAssistant,
			openConversationAssistant,
			isConversationAssistantSidebarActive,
			forceMiniModalFullSize,
		],
	);
};
